import React from 'react';
import { marketingService } from '../../services/marketingService';
import type { RevenueTrend } from '../../types/marketing';

interface RevenueChartProps {
  trends: RevenueTrend[];
  title: string;
}

const RevenueChart: React.FC<RevenueChartProps> = ({ trends, title }) => {
  // Validate trends data
  if (!trends || !Array.isArray(trends) || trends.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
        <div className="flex items-center justify-center h-64 text-gray-500">
          No data available
        </div>
      </div>
    );
  }

  // Filter out invalid data points
  const validTrends = trends.filter(trend => 
    trend && 
    typeof trend.total_revenue === 'number' && 
    !isNaN(trend.total_revenue) &&
    trend.date
  );

  if (validTrends.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
        <div className="flex items-center justify-center h-64 text-gray-500">
          No valid data available
        </div>
      </div>
    );
  }

  // Calculate chart metrics
  const revenues = validTrends.map(t => t.total_revenue);
  const maxRevenue = Math.max(...revenues);
  const minRevenue = 0;

  // Calculate summary statistics
  const totalRevenue = validTrends.reduce((sum, t) => sum + t.total_revenue, 0);
  const averageRevenue = totalRevenue / validTrends.length;
  const peakRevenue = maxRevenue;

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      
      {/* Chart Area */}
      <div className="relative h-64 mb-4 ml-14 sm:ml-16 mt-4">
        <div className="absolute inset-0 flex items-end justify-between space-x-1 px-1">
          {validTrends.map((trend, index) => {
            const heightPct = maxRevenue === 0 ? 0 : (trend.total_revenue / maxRevenue) * 100;
            const barHeight = Math.max(heightPct, trend.total_revenue > 0 ? 4 : 0);
            
            return (
              <div
                key={index}
                className="flex-1 group relative"
                style={{ height: '100%' }}
              >
                {/* Bar - anchored at bottom */}
                <div
                  className="absolute bottom-0 left-0 right-0 bg-blue-500 hover:bg-blue-600 transition-all duration-300 rounded-t"
                  style={{ height: `${barHeight}%` }}
                  title={`${marketingService.formatDateShort(trend.date)}: ${marketingService.formatCurrency(trend.total_revenue)}`}
                >
                  {/* Tooltip */}
                  <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10 whitespace-nowrap">
                    <div>{marketingService.formatDateShort(trend.date)}</div>
                    <div className="font-semibold">{marketingService.formatCurrency(trend.total_revenue)}</div>
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-t-4 border-t-gray-800 border-l-4 border-l-transparent border-r-4 border-r-transparent"></div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Y-axis labels */}
        <div className="absolute left-0 top-0 h-full flex flex-col justify-between text-[10px] sm:text-xs text-gray-500 -ml-14 sm:-ml-16 pr-2 text-right w-14 sm:w-16 break-words">
          <span className="truncate">{marketingService.formatCurrency(maxRevenue)}</span>
          <span className="truncate">{marketingService.formatCurrency((maxRevenue + minRevenue) / 2)}</span>
          <span className="truncate">{marketingService.formatCurrency(minRevenue)}</span>
        </div>
      </div>
      
      {/* X-axis labels */}
      <div className="flex justify-between text-[9px] sm:text-xs text-gray-500 ml-14 sm:ml-16 mt-2 overflow-hidden">
        {validTrends.map((trend, index) => {
          // Show fewer labels on mobile to avoid overlapping
          const shouldShow = validTrends.length <= 4 || index % Math.ceil(validTrends.length / 3) === 0 || index === validTrends.length - 1;
          if (!shouldShow) {
            return <span key={index} className="invisible w-0 sm:w-auto"></span>;
          }
          return (
            <span key={index} className="text-center truncate whitespace-nowrap px-1">
              {marketingService.formatDateShort(trend.date)}
            </span>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-4 flex items-center justify-center space-x-6 text-sm">
        <div className="flex items-center">
          <div className="w-3 h-3 bg-blue-500 rounded mr-2"></div>
          <span className="text-gray-600">Total Revenue</span>
        </div>
      </div>

      {/* Summary */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 text-center">
          <div className="bg-gray-50 sm:bg-transparent p-2 sm:p-0 rounded-lg">
            <div className="text-xs sm:text-sm text-gray-600">Total</div>
            <div className="font-semibold text-sm sm:text-base">
              {marketingService.formatCurrency(totalRevenue)}
            </div>
          </div>
          <div className="bg-gray-50 sm:bg-transparent p-2 sm:p-0 rounded-lg">
            <div className="text-xs sm:text-sm text-gray-600">Average</div>
            <div className="font-semibold text-sm sm:text-base">
              {marketingService.formatCurrency(averageRevenue)}
            </div>
          </div>
          <div className="bg-gray-50 sm:bg-transparent p-2 sm:p-0 rounded-lg">
            <div className="text-xs sm:text-sm text-gray-600">Peak (Puncak)</div>
            <div className="font-semibold text-sm sm:text-base">
              {marketingService.formatCurrency(peakRevenue)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RevenueChart;